import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { Toast, handleFormSubmissionError } from 'ui/components'

import { api } from '../../../../api'
import { ManageBankUserRequest } from '../../../../types'
import { NEW_USER } from '../../constants'
import { ManageUserDialogProps } from './ManageUserDialog.types'
import { getFormFromTableItem } from './getFormFromTableItem'

export const useManageUserDialog = (props: ManageUserDialogProps) => {
  const { initialValue, onSaved, roles, onClose } = props

  const saveUser = useMutation<void, Error, ManageBankUserRequest>({
    mutationFn: (user) => (user.id ? api.update(user) : api.create(user)),
    onSuccess: async (_, payload) => {
      onSaved(initialValue?.isCurrentUser ?? false)
      formApi.update({ defaultValues: getFormFromTableItem(NEW_USER) })
      formApi.reset()
      onClose()
      Toast.success('Success', `User was ${payload.id ? 'updated' : 'created'}.`)
    },
    onError: (error) => {
      handleFormSubmissionError<ManageBankUserRequest>(error, formApi)
    }
  })

  const formApi = useForm({
    defaultValues: initialValue ? getFormFromTableItem(initialValue) : undefined,
    onSubmit: ({ value }) => saveUser.mutateAsync(value)
  })

  const { Field, handleSubmit } = formApi
  const isDirty = useStore(formApi.store, (state) => state.isDirty)
  const isSubmitting = useStore(formApi.store, (state) => state.isSubmitting)
  const values = useStore(formApi.store, (state) => state.values)

  const rolesOptions = [{ label: 'No role', value: '' }].concat(
    roles.map((role) => ({ label: role.name, value: role.key }))
  )

  const onCloseDialog = () => {
    formApi.reset()
    onClose()
  }

  return {
    Field,
    isDirty,
    isSubmitting,
    handleSubmit,
    rolesOptions,
    isFormReady: !!Object.keys(values).length,
    onCloseDialog
  }
}
